import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "@/lib/api";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { formatInteger } from "@/lib/format";
import {
  AdminTableControls,
  AdminTableFilterSelect,
  SortableTh,
  useAdminTable,
} from "@/components/admin/AdminTableControls";
import AdminFormHeader from "@/components/admin/AdminFormHeader";

type GuideTopic = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  heroMediaId: string | null;
  color: string | null;
  sortOrder: number;
  status: "active" | "disabled";
};

type GuideTopicSortKey = "name" | "sort" | "status";

type FormState = {
  name: string;
  slug: string;
  description: string;
  color: string;
  sortOrder: string;
  status: "active" | "disabled";
};

const empty: FormState = {
  name: "",
  slug: "",
  description: "",
  color: "",
  sortOrder: "0",
  status: "active",
};

function toForm(t: GuideTopic): FormState {
  return {
    name: t.name,
    slug: t.slug,
    description: t.description ?? "",
    color: t.color ?? "",
    sortOrder: t.sortOrder.toString(),
    status: t.status,
  };
}

function topicSearchText(topic: GuideTopic) {
  return [topic.name, topic.slug, topic.description, topic.color, topic.status, formatInteger(topic.sortOrder)]
    .filter(Boolean)
    .join(" ");
}

const topicSorters: Record<GuideTopicSortKey, (row: GuideTopic) => string | number> = {
  name: (row) => row.name,
  sort: (row) => row.sortOrder,
  status: (row) => row.status,
};

export default function AdminGuideTopics() {
  const [list, setList] = useState<GuideTopic[]>([]);
  const [editing, setEditing] = useState<GuideTopic | "new" | null>(null);
  const [form, setForm] = useState<FormState>(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  async function refresh() {
    const r = await api.get<{ guideTopics: GuideTopic[] }>("/api/admin/guide-topics");
    setList(r.guideTopics);
  }

  useEffect(() => {
    refresh().finally(() => setLoaded(true));
  }, []);

  function startNew() {
    setForm(empty);
    setEditing("new");
    setErr(null);
  }
  function startEdit(t: GuideTopic) {
    setForm(toForm(t));
    setEditing(t);
    setErr(null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const payload = {
      name: form.name,
      slug: form.slug || undefined,
      description: form.description || null,
      color: form.color || null,
      sortOrder: parseInt(form.sortOrder || "0", 10),
      status: form.status,
    };
    try {
      if (editing === "new") await api.post("/api/admin/guide-topics", payload);
      else if (editing) await api.put(`/api/admin/guide-topics/${editing.id}`, payload);
      await refresh();
      setEditing(null);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove(t: GuideTopic) {
    if (!confirm(`Delete "${t.name}"? This cannot be undone.`)) return;
    try {
      await api.del(`/api/admin/guide-topics/${t.id}`);
      await refresh();
    } catch (e2) {
      alert(e2 instanceof Error ? e2.message : "Delete failed");
    }
  }

  const filters = useMemo(
    () => [(row: GuideTopic) => statusFilter === "all" || row.status === statusFilter],
    [statusFilter],
  );
  const {
    rows: tableRows,
    sort,
    requestSort,
  } = useAdminTable<GuideTopic, GuideTopicSortKey>({
    rows: list,
    search,
    initialSort: { key: "sort", direction: "asc" },
    sorters: topicSorters,
    searchText: topicSearchText,
    filters,
  });

  return (
    <section>
      <style>{css}</style>
      <div className="hdr">
        <h1 className="aflt-admin-title">Guide Topics</h1>
        {!editing && (
          <button type="button" className="btn-woven" onClick={startNew}>
            + New topic
          </button>
        )}
      </div>

      {editing ? (
        <form onSubmit={submit} className="paper-card">
          <AdminFormHeader
            title={editing === "new" ? "New guide topic" : editing.name}
            eyebrow={editing === "new" ? "Create guide topic" : "Edit guide topic"}
            backLabel="Back to guide topics"
            onBack={() => setEditing(null)}
          />
          <div className="grid2">
            <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
            <Field label="Slug (optional)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} />
          </div>
          <Field
            label="Description"
            value={form.description}
            onChange={(v) => setForm({ ...form, description: v })}
            textarea
          />
          <div className="grid2">
            <Field label="Color" value={form.color} onChange={(v) => setForm({ ...form, color: v })} />
            <Field
              label="Sort order"
              value={form.sortOrder}
              onChange={(v) => setForm({ ...form, sortOrder: v })}
            />
          </div>
          <label className="fld">
            <span>Status</span>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as "active" | "disabled" })}
            >
              <option value="active">active</option>
              <option value="disabled">disabled</option>
            </select>
          </label>
          {err && <p className="err">{err}</p>}
          <div className="actions">
            <button type="submit" className="btn-woven" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </form>
      ) : !loaded ? (
        <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>Loading…</p>
      ) : (
        <>
          <AdminTableControls
            search={search}
            onSearchChange={setSearch}
            placeholder="Search guide topics..."
            totalCount={list.length}
            resultCount={tableRows.length}
          >
            <AdminTableFilterSelect
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "all", label: "All statuses" },
                { value: "active", label: "Active" },
                { value: "disabled", label: "Disabled" },
              ]}
            />
          </AdminTableControls>
          {list.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No guide topics yet.</p>
          ) : tableRows.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No topics match those filters.</p>
          ) : (
            <table className="paper-table">
              <thead>
                <tr>
                  <SortableTh label="Name" sortKey="name" sort={sort} onSort={requestSort} />
                  <SortableTh label="Sort" sortKey="sort" sort={sort} onSort={requestSort} />
                  <SortableTh label="Status" sortKey="status" sort={sort} onSort={requestSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {tableRows.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <strong>{t.name}</strong>
                      <span className="slug">{t.slug}</span>
                    </td>
                    <td>{formatInteger(t.sortOrder)}</td>
                    <td>{t.status}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" className="btn-ghost sm" onClick={() => startEdit(t)}>
                        Edit
                      </button>
                      <button type="button" className="btn-danger sm" onClick={() => remove(t)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  textarea,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  textarea?: boolean;
}) {
  return (
    <label className="fld">
      <span>{label}</span>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} required={required} />
      ) : (
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} required={required} />
      )}
    </label>
  );
}

const css = `
  .hdr { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 28px; }
  .btn-woven {
    display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px;
    background: ${colors.berryRose}; color: #fff; font-family: ${fonts.body};
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border: none; border-radius: 7px; cursor: pointer;
    outline: 2px dashed rgba(255,255,255,0.45); outline-offset: -5px; box-shadow: 0 2px 8px rgba(212,96,106,0.30);
  }
  .btn-woven:hover { background: #c0535d; }
  .btn-woven:disabled { opacity: 0.6; cursor: default; }
  .btn-ghost {
    background: ${colors.cream}; border: 1px solid rgba(201,147,76,0.55); color: ${colors.navy};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer;
  }
  .btn-ghost:hover { box-shadow: 0 6px 14px -8px rgba(8,45,61,0.35); }
  .btn-danger { background: ${colors.cream}; border: 1px solid ${colors.berryRose}; color: ${colors.berryRose};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer; }
  .btn-danger:hover { background: ${colors.berryRose}; color: #fff; }
  .sm { padding: 6px 12px; margin-left: 6px; font-size: 0.62rem; }

  .paper-table { width: 100%; border-collapse: separate; border-spacing: 0; background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; overflow: hidden; }
  .paper-table th, .paper-table td { text-align: left; padding: 0.7rem 0.9rem; border-bottom: 1px dashed rgba(201,147,76,0.18); font-family: ${fonts.body}; vertical-align: middle; }
  .paper-table thead th { background: ${colors.blushLight}; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.18em; color: rgba(8,45,61,0.7); border-bottom: 1px solid rgba(201,147,76,0.15); }
  .paper-table tbody tr:last-child td { border-bottom: none; }
  .paper-table tbody td { font-size: 13.5px; color: rgba(8,45,61,0.85); }
  .paper-table .slug { display: block; font-size: 11px; color: rgba(8,45,61,0.47); }

  .paper-card { background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; padding: 1.5rem 1.75rem; }
  .form-legend { font-family: ${fonts.logo}; font-weight: 500; font-size: 1.2rem; color: ${colors.navy}; display: block; margin-bottom: 1.25rem; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  .fld { display: block; margin-bottom: 1rem; }
  .fld > span { display: block; font-family: ${fonts.body}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: rgba(8,45,61,0.65); margin-bottom: 0.35rem; }
  .fld input[type=text], .fld select {
    width: 100%; padding: 0.55rem 0.9rem; border: 1px solid rgba(201,147,76,0.40); border-radius: 999px;
    background: ${colors.cream}; color: ${colors.navy}; font-family: ${fonts.body}; font-weight: 600; font-size: 13.5px; outline: none;
  }
  .fld textarea {
    width: 100%; min-height: 90px; padding: 0.7rem 0.9rem; border: 1px solid rgba(201,147,76,0.40); border-radius: 12px;
    background: ${colors.cream}; color: ${colors.navy}; font-family: ${fonts.body}; font-weight: 500; font-size: 13.5px; outline: none;
  }
  .fld input:focus, .fld select:focus, .fld textarea:focus { border-color: ${colors.berryRose}; box-shadow: 0 4px 14px -8px rgba(212,96,106,0.55); }
  .actions { display: flex; gap: 0.6rem; margin-top: 0.5rem; }
  .err { color: ${colors.berryRose}; font-family: ${fonts.body}; font-weight: 600; font-size: 0.85rem; margin: 0.5rem 0; }
`;
